import { publishPosLoyaltyEarnAck, type CanonicalLoyaltyEarnAck } from "../loyalty/loyalty-earn-ack";
import type { PromotionClaimEvidence } from "../promotion/promotion-claim-session";

export type CashSaleGatewayLine = {
  line_id: string;
  menu_item_id: string;
  menu_variant_id: string | null;
  quantity: number;
  local_unit_price: number;
  price_version_id: string;
  modifier_option_ids: string[];
};

export type { PromotionClaimEvidence };

export type CashSaleSyncInput = {
  deviceSecret: string;
  sessionToken: string;
  localTransactionId: string;
  lines: CashSaleGatewayLine[];
  catalogRevision: number;
  localTotal: number;
  tenderedAmount: number;
  localChangeAmount: number;
  localCommittedAt: string;
  executionMode: "ONLINE" | "OFFLINE";
  requestKey: string;
  clientContext?: Record<string, unknown>;
  promotionClaimEvidence?: PromotionClaimEvidence[];
};

export type CanonicalCashPayment = {
  payment_id: string;
  payment_no: number;
  payment_status: string;
  payment_method_code: string;
  payment_method_name: string;
  currency_code: string;
  amount: number;
  tendered_amount: number;
  change_amount: number;
  settled_at: string;
  request_key: string;
};

export type CanonicalPromotionClaim = {
  claim_instance_id: string;
  claim_definition_id: string;
  promotion_version_id: string;
  claim_status: string;
  consumed_at: string | null;
  consumed_order_id: string | null;
  credential_hint: string | null;
  binding_mode: string;
};

export type CashSaleSyncResult = {
  ok: boolean;
  contract: string;
  result_status: string;
  idempotent_replay?: boolean;
  atomic?: boolean;
  settlement_kind: "PAYMENT" | "NO_PAYMENT_REQUIRED";
  order_id: string;
  order_no: string;
  order_status: string;
  organization_id: string;
  branch_id: string;
  commerce_channel_id: string;
  commerce_node_id: string;
  test_run_id: string | null;
  currency_code: string;
  grand_total: number;
  paid_amount: number;
  confirmed_at: string;
  paid_at: string;
  root_request_key: string;
  promotion_claims?: CanonicalPromotionClaim[];
  payment: CanonicalCashPayment | null;
  loyalty_earn?: CanonicalLoyaltyEarnAck | null;
};

export type CashSaleGatewayResult = {
  ok: boolean;
  data?: CashSaleSyncResult;
  error?: string;
};

export class PosCashSaleGatewayClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;

  constructor(options: { baseUrl: string; timeoutMs?: number }) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.timeoutMs = options.timeoutMs ?? 15_000;
  }

  async sync(input: CashSaleSyncInput): Promise<CashSaleGatewayResult> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), this.timeoutMs);

    try {
      const response = await fetch(`${this.baseUrl}/api/pos/runtime/cash-sale`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          device_secret: input.deviceSecret,
          session_token: input.sessionToken,
          local_transaction_id: input.localTransactionId,
          lines: input.lines,
          catalog_revision: input.catalogRevision,
          local_total: input.localTotal,
          tendered_amount: input.tenderedAmount,
          local_change_amount: input.localChangeAmount,
          local_committed_at: input.localCommittedAt,
          execution_mode: input.executionMode,
          request_key: input.requestKey,
          client_context: input.clientContext ?? {},
          promotion_claim_evidence: input.promotionClaimEvidence ?? [],
        }),
        signal: controller.signal,
        credentials: "omit",
      });

      const payload = (await response.json()) as CashSaleGatewayResult;
      if (!response.ok) {
        return { ok: false, error: payload.error ?? `GATEWAY_HTTP_${response.status}` };
      }
      if (payload.ok && payload.data) {
        publishPosLoyaltyEarnAck({
          orderId: payload.data.order_id,
          orderNo: payload.data.order_no,
          loyaltyEarn: payload.data.loyalty_earn ?? null,
        });
      }
      return payload;
    } catch (error) {
      return {
        ok: false,
        error:
          error instanceof DOMException && error.name === "AbortError"
            ? "GATEWAY_TIMEOUT"
            : "GATEWAY_UNREACHABLE",
      };
    } finally {
      window.clearTimeout(timeout);
    }
  }
}
